import i18n from '@/i18n';

/**
 * Operator-facing sentences for the customer special price API (api/admin/customer-special-prices).
 * The backend sends its own Lao `message` too; this copy exists so an error CODE or a stock
 * Laravel English sentence never reaches the screen as-is. The maps hold i18n KEYS and are
 * translated at call time, so the page's current language always wins.
 */
const ERROR_CODE_TEXT = {
  NOT_FOUND: 'specialPrice.error_not_found',
  WRITE_FAILED: 'specialPrice.error_write_failed',
};

const STATUS_TEXT = {
  401: 'specialPrice.error_status_401',
  403: 'specialPrice.error_status_403',
  404: 'specialPrice.error_status_404',
  422: 'specialPrice.error_status_422',
  429: 'specialPrice.error_status_429',
  500: 'specialPrice.error_status_500',
  502: 'specialPrice.error_status_502',
  503: 'specialPrice.error_status_503',
  504: 'specialPrice.error_status_504',
};

const LARAVEL_DEFAULT_TEXT = /^(The given data was invalid|Server Error|Unauthenticated|Unauthorized|This action is unauthorized|Forbidden|Not Found|Too Many Attempts|User does not have)/i;

/**
 * One sentence for a failed request: the first field error, else the code's text, else the
 * backend's own Lao message, else a sentence for the HTTP status.
 */
export function friendlyMessage(error) {
  const response = error && error.response;
  const body = (response && response.data) || {};
  const status = response && response.status;

  const fieldMessages = Object.keys(body.errors || {})
    .map(key => [].concat(body.errors[key])[0])
    .filter(text => typeof text === 'string' && text.trim() !== '');
  if (fieldMessages.length) {
    return fieldMessages[0];
  }

  if (ERROR_CODE_TEXT[body.error]) {
    return i18n.t(ERROR_CODE_TEXT[body.error]);
  }

  if (typeof body.message === 'string' && body.message.trim() !== '' && !LARAVEL_DEFAULT_TEXT.test(body.message)) {
    return body.message;
  }

  if (!response) {
    return i18n.t('specialPrice.error_no_connection');
  }

  return i18n.t(STATUS_TEXT[status] || 'specialPrice.error_unexpected');
}

/**
 * Laravel's 422 `errors` as { 'customers.3.tel': 'message', ... } (first message per field).
 */
export function fieldErrors(error) {
  const body = (error && error.response && error.response.data) || {};
  const out = {};
  Object.keys(body.errors || {}).forEach(key => {
    out[key] = [].concat(body.errors[key])[0];
  });
  return out;
}

/**
 * The route types a program can be limited to (backend CustomerSpecialPriceProgramCondition), compared
 * on the bill's origin and destination PROVINCE. `value` is the API code; `labelKey` the i18n key
 * of its label.
 */
export const ROUTE_SCOPES = [
  { value: 'any', labelKey: 'specialPrice.route_scope_any' },
  { value: 'within_province', labelKey: 'specialPrice.route_scope_within_province' },
  { value: 'across_province', labelKey: 'specialPrice.route_scope_across_province' },
];

export function routeScopeLabel(value) {
  const found = ROUTE_SCOPES.find(scope => scope.value === (value || 'any'));
  return found ? i18n.t(found.labelKey) : value;
}

export function formatKip(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString('en-US') : String(value);
}
